import { Link } from 'react-router-dom';
import type { Post } from '@/mocks/posts';

type Variant = 'grid' | 'wide' | 'compact';

type Props = {
  post: Post;
  variant?: Variant;
  rank?: number;
};

const categoryTone: Record<string, string> = {
  star: 'bg-primary-100 text-primary-700',
  influencer: 'bg-accent-100 text-accent-900',
  submit: 'bg-secondary-100 text-secondary-900',
  deep: 'bg-foreground-100 text-foreground-800',
};

export default function PostCard({ post, variant = 'grid', rank }: Props) {
  const to = `/post/${post.id}`;
  const tone = categoryTone[post.categorySlug] ?? 'bg-secondary-100 text-secondary-900';

  if (variant === 'compact') {
    return (
      <Link
        to={to}
        className="group flex items-start gap-3 py-3 transition-colors hover:bg-background-100"
      >
        {typeof rank === 'number' && (
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
              rank <= 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
            }`}
          >
            {rank}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-medium leading-relaxed text-foreground-900 transition-colors group-hover:text-primary-600">
            {post.title}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span>{post.categoryLabel}</span>
            <span className="text-background-300">·</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
              {post.views.toLocaleString('zh-CN')}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={to}
        className="group grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:grid-cols-[220px_1fr]"
      >
        <div className="h-40 w-full overflow-hidden rounded-md sm:h-full sm:min-h-[140px]">
          <img
            src={post.cover}
            alt={post.coverAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col justify-between py-1">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}>
                {post.categoryLabel}
              </span>
              {post.hot && (
                <span className="flex items-center gap-1 rounded-full bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                  <i className="ri-fire-fill" aria-hidden="true"></i>
                  热
                </span>
              )}
            </div>
            <h3 className="font-heading text-lg font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
              {post.title}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{post.summary}</p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span>{post.publishedAt}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {post.views.toLocaleString('zh-CN')}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true"></i>
              {post.readMinutes} 分钟
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-48 w-full overflow-hidden">
        <img
          src={post.cover}
          alt={post.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}>
          {post.categoryLabel}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          {post.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{post.summary}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span>{post.publishedAt.slice(5, 16)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
            {post.heat}
          </span>
        </div>
      </div>
    </Link>
  );
}